import { DATE_FORMAT } from 'dash-common';
import {
  Button,
  DatePeriodTypesEnum,
  DateRangePicker,
  Devider,
  FilterContext,
  Flex,
  Form,
  Select,
  Text,
  getPeriodRangeByPeriodType,
} from 'dash-ui';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import { FormWraper } from 'pages/Dashboard/components/DashboardFilters/components/TimeRangeModal.style';
import { FormEvent, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

const TimeRangeModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['common', 'pages']);
  const theme = useTheme();
  const { setFilterState, state: filterState } = useContext(FilterContext);
  const [selectedRange, setSelectedRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(filterState?.dateRange ?? null);
  const [periodType, setPeriodType] = useState<DatePeriodTypesEnum | null>(
    filterState?.periodType ?? null
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setFilterState('dateRange', selectedRange);
    setFilterState('periodType', periodType);
    onClose();
  };

  const onCancelClick = () => {
    onClose();
    setFilterState('dateRange', null);
    setFilterState('periodType', null);
    setSelectedRange(null);
  };

  const rangeSelectOptions = useMemo(() => {
    const options = Object.values(DatePeriodTypesEnum);

    return options.splice(0, options.length - 1).map((value) => ({
      label: startCase(
        t(`common:labels.dateRangePeriods.${camelCase(value)}`) || ''
      ),
      value,
    }));
  }, [t]);

  return (
    <FormWraper p={4} onSubmit={onSubmit}>
      <Text semibold>{t('pages:organizationDashboardPage.filterByDate')}</Text>
      <Form.Group>
        <Form.Label>
          {t('pages:organizationDashboardPage.selectRange')}
        </Form.Label>
        <Select
          onMouseDown={(e) => e.stopPropagation()}
          placeholder={startCase(
            t(
              `common:labels.dateRangePeriods.${camelCase(
                DatePeriodTypesEnum.CURRENT_WEEK
              )}`
            ) || ''
          )}
          value={periodType || ''}
          options={rangeSelectOptions}
          onChanged={(value) => {
            const type = value as DatePeriodTypesEnum;
            setSelectedRange(getPeriodRangeByPeriodType(type));
            setPeriodType(type);
          }}
          w="100%"
        />
      </Form.Group>
      <Devider w="100%" my={3} color={theme.colors.n40} />
      <Form.Group>
        <Form.Label>
          {t('pages:organizationDashboardPage.customRange')}
        </Form.Label>

        <DateRangePicker
          popperProps={{ strategy: 'fixed' }}
          selected={selectedRange?.startDate}
          onChange={(range: { startDate: Date; endDate: Date }) =>
            setSelectedRange(range)
          }
          startDate={selectedRange?.startDate}
          endDate={selectedRange?.endDate}
          placeholderText={DATE_FORMAT}
        />
      </Form.Group>
      <Flex mt={4}>
        <Button mr={2} px={6}>
          {t('common:labels.save')}
        </Button>
        <Button onClick={onCancelClick} type="button" variation="outline">
          {t('common:labels.cancel')}
        </Button>
      </Flex>
    </FormWraper>
  );
};

export default TimeRangeModal;
